/**
 * Shared fetchers for initial app bootstrap (prefetched before first paint).
 */
import axios from 'axios'
import { api, getAccessToken, setAccessToken } from '@/lib/api'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'
import type { FeatureFlags } from '@/hooks/useFeatures'
import type { ThemeConfig } from '@/providers/ThemeProvider'
import type { HeaderConfig } from '@/hooks/useCmsHeader'
import type { FooterConfig } from '@/hooks/useCmsFooter'
import type { MenuTree } from '@/hooks/usePublicMenu'
import type { HomeBannerPayload } from '@/hooks/useHomeBanners'
import type { User, AuthTokens, Cart } from '@/types'
import { HOME_PAGE_SLUG } from '@shared/constants/cms'

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api/v1'

export async function fetchBootstrapFeatures(): Promise<FeatureFlags> {
  const { data } = await api.get<{ data: FeatureFlags }>('/config/features')
  return data.data
}

export async function fetchBootstrapTheme(): Promise<ThemeConfig> {
  const { data } = await api.get<{ data: ThemeConfig }>('/cms/theme')
  return data.data
}

export async function fetchBootstrapAuth(): Promise<User | null> {
  if (!getAccessToken()) {
    try {
      const { data } = await axios.post<AuthTokens>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      )
      setAccessToken(data.access_token)
    } catch {
      return null
    }
  }

  try {
    const { data } = await api.get<{ data: User }>('/auth/me')
    return data.data
  } catch {
    setAccessToken(null)
    return null
  }
}

export async function fetchBootstrapHeader(): Promise<HeaderConfig> {
  const { data } = await api.get<{ data: HeaderConfig }>('/cms/header')
  return data.data
}

export async function fetchBootstrapFooter(): Promise<FooterConfig> {
  const { data } = await api.get<{ data: FooterConfig }>('/cms/footer')
  return data.data
}

export async function fetchBootstrapMenu(slug: string): Promise<MenuTree | null> {
  try {
    const { data } = await api.get<{ data: MenuTree }>(`/menus/${slug}`)
    return data.data
  } catch {
    return null
  }
}

const DEFAULT_CURRENCY_CONFIG = {
  currency: import.meta.env.VITE_BASE_CURRENCY ?? 'INR',
  symbol: '₹',
} as const

export async function fetchBootstrapCurrency(): Promise<{ currency: string; symbol: string }> {
  try {
    const { data } = await api.get<{ currency: string; symbol: string }>('/config/currency')
    useUIStore.setState({ currency: data.currency, currencyLoaded: true })
    return data
  } catch {
    useUIStore.setState({
      currency: DEFAULT_CURRENCY_CONFIG.currency,
      currencyLoaded: true,
    })
    return { ...DEFAULT_CURRENCY_CONFIG }
  }
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve()
    img.onerror = () => resolve()
    img.src = url
  })
}

export async function fetchBootstrapHomeBanners(): Promise<HomeBannerPayload> {
  try {
    const { data } = await api.get<{ data: HomeBannerPayload }>('/home-banners')
    const payload = data.data
    const firstUrl = payload.slides[0]?.image_url
    if (firstUrl) {
      await preloadImage(firstUrl)
    }
    return payload
  } catch {
    return { slides: [], settings: null }
  }
}

export async function fetchBootstrapHomePage(): Promise<{ slug: string; body: string } | null> {
  try {
    const { data } = await api.get<{ data: { slug: string; body: string } }>(
      `/cms/pages/${HOME_PAGE_SLUG}`,
    )
    return data.data
  } catch {
    return null
  }
}

export async function fetchBootstrapCart(): Promise<Cart | null> {
  const sessionToken = useCartStore.getState().sessionToken
  const headers = sessionToken ? { 'X-Session-Token': sessionToken } : {}

  const { data } = await api.get<{
    cart: { id: number; session_token: string | null; coupon_id: number | null; expires_at: string | null } | null
    items: Cart['items']
    subtotal: number
    discount_amount?: number
    coupon_code?: string | null
    coupon_discount?: number
    loyalty_discount?: number
    tax_amount?: number
    cgst_amount?: number
    sgst_amount?: number
    grand_total?: number
    applied_promotions?: Cart['applied_promotions']
  }>('/cart', { headers })

  if (!data.cart) {
    useCartStore.getState().setCart(null)
    return null
  }

  const items = (data.items ?? []).map((item: Cart['items'][number] & { current_price?: number; unit_price_snapshot?: number }) => ({
    ...item,
    unit_price: item.current_price ?? item.unit_price_snapshot ?? 0,
    subtotal: (item.current_price ?? item.unit_price_snapshot ?? 0) * (item.quantity ?? 1),
  }))

  const cart: Cart = {
    id: data.cart.id,
    session_token: data.cart.session_token,
    items,
    subtotal: data.subtotal ?? 0,
    discount_amount: data.discount_amount ?? 0,
    coupon_code: data.coupon_code ?? null,
    coupon_discount: data.coupon_discount ?? 0,
    loyalty_discount: data.loyalty_discount ?? 0,
    tax_amount: data.tax_amount ?? 0,
    cgst_amount: data.cgst_amount ?? 0,
    sgst_amount: data.sgst_amount ?? 0,
    shipping_amount: 0,
    grand_total: data.grand_total ?? data.subtotal ?? 0,
    applied_promotions: data.applied_promotions ?? [],
  }

  if (data.cart.session_token) {
    useCartStore.getState().setSessionToken(data.cart.session_token)
  }
  useCartStore.getState().setCart(cart)
  return cart
}
